import {
  HttpErrorResponse,
  HttpResponse,
  HttpStatusCode,
  type HttpEvent,
  type HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { IS_INIT_SESSION } from '@copec-gid/gid-arquitectura.seguridad-autorizacion-modulo';
import { catchError, from, map, mergeMap, of, switchMap, throwError, type Observable } from 'rxjs';

import { AuthSession } from './auth-session';

const REJECTED_STATUS: readonly number[] = [HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden];

class RejectedResponse {
  constructor(readonly response: HttpResponse<unknown>) {}
}

function bodyStatus(body: unknown): number | null {
  if (body === null || typeof body !== 'object') {
    return null;
  }

  const code: unknown = Reflect.get(body, 'status_code');
  return typeof code === 'string' || typeof code === 'number' ? Number(code) : null;
}

function isRejected(status: number, body: unknown): boolean {
  return REJECTED_STATUS.includes(bodyStatus(body) ?? status);
}

export const tunnelRetryInterceptor: HttpInterceptorFn = (request, next) => {
  if (request.context.get(IS_INIT_SESSION)) {
    return next(request);
  }

  const auth = inject(AuthSession);

  const renewThen = (failure: Observable<HttpEvent<unknown>>): Observable<HttpEvent<unknown>> =>
    from(auth.renewTunnel()).pipe(
      map(() => true),
      catchError(() => of(false)),
      switchMap((renewed) => (renewed ? next(request) : failure)),
    );

  return next(request).pipe(
    mergeMap((event) =>
      event instanceof HttpResponse && isRejected(event.status, event.body)
        ? throwError(() => new RejectedResponse(event))
        : of(event),
    ),
    catchError((error: unknown) => {
      const failure: Observable<HttpEvent<unknown>> =
        error instanceof RejectedResponse ? of(error.response) : throwError(() => error);
      const rejected =
        error instanceof RejectedResponse ||
        (error instanceof HttpErrorResponse && isRejected(error.status, error.error));
      return rejected ? renewThen(failure) : failure;
    }),
  );
};
